export const formatPercent = (value: number, decimals = 1) => `${value.toFixed(decimals)} %`;

export const formatTrend = (delta: number) => {
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(1)} pts`;
};

export const formatCurrency = (value: number, currency = 'EUR') =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(value);

export const formatCount = (value: number) => new Intl.NumberFormat('fr-FR').format(value);

/**
 * Identifiant de course à la forme attendue par le client : `Fare_6013443633971200`
 * devient `R6013443633971200`. `Fare_` est un préfixe interne Allocab, sans
 * signification pour qui lit un listing ou ouvre une réclamation.
 *
 * Tout identifiant d'une autre forme (uuid de facture, id d'incident) est rendu
 * tel quel : la fonction est sûre à appliquer partout.
 */
export const formatAllocabId = (id: string): string => id.replace(/^Fare_/, 'R');

/**
 * Nom du passager, « Prénom Nom », ou chaîne vide.
 *
 * Accepte **les deux formes** que l'API emploie : un objet `{ firstName, lastName }`
 * sur `GET /v1/trips`, une chaîne toute faite sur l'autocomplete. Ne jamais
 * rendre `passenger` tel quel dans le JSX : l'objet fait planter React (erreur
 * #31) et avec lui tout l'écran — c'est arrivé le 22 septembre 2026, quand le
 * back a livré l'objet là où une chaîne était attendue.
 */
export const formatPassenger = (passenger: PassengerInput): string => {
  if (!passenger) return '';
  if (typeof passenger === 'string') return passenger.trim();
  return [passenger.firstName, passenger.lastName]
    .map(part => part?.trim())
    .filter(Boolean)
    .join(' ');
};

type PassengerInput = string | { firstName?: string | null; lastName?: string | null } | null | undefined;

const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avril', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

const pad = (n: number) => n.toString().padStart(2, '0');

export const formatAbsoluteDateTime = (iso: string | number) => {
  const d = new Date(iso);
  return `${pad(d.getDate())} ${MONTHS_FR[d.getMonth()]} ${d.getFullYear()} à ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const formatShortDateTime = (iso: string | number) => {
  const d = new Date(iso);
  return `${pad(d.getDate())} ${MONTHS_FR[d.getMonth()]} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Format compact pour table dense (max 12 chars).
 * "12 min", "3 h", "hier", "19 mars", "mars 2025"
 */
export const formatRelativeDate = (iso: string | number, now: Date = new Date()) => {
  const d = new Date(iso);
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffH = Math.floor(diffMs / 3_600_000);

  if (diffMin < 1) return 'à l’instant';
  if (diffMin < 60) return `${diffMin} min`;
  if (diffH < 24) return `${diffH} h`;

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.floor((startOfToday.getTime() - startOfDate.getTime()) / 86_400_000);

  if (dayDiff === 1) return 'hier';
  if (d.getFullYear() === now.getFullYear()) {
    return `${pad(d.getDate())} ${MONTHS_FR[d.getMonth()]}`;
  }
  return `${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`;
};

export const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};
